import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Database, Gauge } from 'lucide-react'
import type { TokenUsageCopy, TokenUsageReport } from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import UsageChannelIcon from './UsageChannelIcon'

interface Props {
  report: TokenUsageReport
  copy: TokenUsageCopy
}

export default function UsageSummary({ report, copy }: Props) {
  const reduceMotion = useReducedMotion()
  const usage = report.usage.percentage
  const progress = Math.min(Math.max(usage ?? 0, 0), 100) / 100
  const channels = report.today_channels.slice(0, 5)

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card shadow-xs">
      <div className="grid lg:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)]">
        <div className="p-5 md:p-6 lg:border-r lg:border-border">
          <div className="flex items-center gap-2">
            <Gauge className="size-3.5 text-agent" strokeWidth={1.8} />
            <h2 className="text-[13px] font-semibold text-foreground">{copy.summary.usage}</h2>
          </div>

          <AnimatePresence mode="popLayout" initial={false}>
            <motion.p
              key={report.usage.percentage_label ?? 'unavailable'}
              initial={reduceMotion ? false : { opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduceMotion ? undefined : { opacity: 0, y: -3 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              className="mt-5 text-[40px] font-semibold leading-none tabular-nums tracking-[-0.04em] text-foreground md:text-[44px]"
            >
              {report.usage.has_limit ? `${report.usage.percentage_label}%` : copy.summary.unavailable}
            </motion.p>
          </AnimatePresence>
          <p className="mt-2 text-[12px] text-muted-foreground">
            {report.usage.has_limit ? copy.summary.usage_hint : copy.summary.limit_unavailable}
          </p>

          <div className="mt-5 h-1 overflow-hidden rounded-full bg-secondary">
            <motion.span
              aria-hidden="true"
              className={`block h-full origin-left rounded-full ${usage !== null && usage > 100 ? 'bg-destructive' : 'bg-agent'}`}
              initial={reduceMotion ? false : { scaleX: 0 }}
              animate={{ scaleX: progress }}
              transition={{ duration: 0.52, ease: [0.16, 1, 0.3, 1] }}
            />
          </div>

          <div className="mt-5 flex items-center justify-between border-t border-border pt-4">
            <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Database className="size-3 text-agent" strokeWidth={1.8} />
              {copy.summary.cache}
            </span>
            <span className="text-[12px] font-medium tabular-nums text-foreground">
              {report.usage.cache_rate_label ? `${report.usage.cache_rate_label}%` : copy.summary.unavailable}
            </span>
          </div>
        </div>

        <div className="border-t border-border p-5 md:p-6 lg:border-t-0">
          <h2 className="text-[13px] font-semibold text-foreground">{copy.channel_title}</h2>
          <p className="mt-1 text-[11.5px] text-muted-foreground">{copy.channel_description}</p>

          {channels.length === 0 ? (
            <p className="py-10 text-center text-[12px] text-muted-foreground">{copy.no_today_data}</p>
          ) : (
            <div className="mt-4 space-y-3">
              {channels.map((channel, index) => (
                <div key={channel.key} className="grid grid-cols-[1.75rem_minmax(0,1fr)_3.25rem] items-center gap-2.5">
                  <span className="flex size-7 items-center justify-center rounded-md border border-border bg-secondary/45">
                    <UsageChannelIcon channel={channel.key} />
                  </span>
                  <div className="min-w-0">
                    <span className="block truncate text-[12px] font-medium text-foreground">{channel.label}</span>
                    <span aria-hidden="true" className="mt-1.5 block h-[2px] overflow-hidden rounded-full bg-secondary">
                      <motion.span
                        className="block h-full origin-left rounded-full bg-agent/70"
                        initial={reduceMotion ? false : { scaleX: 0 }}
                        animate={{ scaleX: Math.min(channel.share, 100) / 100 }}
                        transition={{ duration: 0.4, delay: reduceMotion ? 0 : index * 0.04, ease: [0.16, 1, 0.3, 1] }}
                      />
                    </span>
                  </div>
                  <span className="text-right text-[12px] font-medium tabular-nums text-muted-foreground">
                    {channel.share_label ?? '0'}%
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
